// CSRF Token Service
// Manages CSRF token fetching and caching for secure API requests

import { logger } from '../utils/logger';

class CSRFService {
  private csrfToken: string | null = null;
  private tokenFetchPromise: Promise<string | null> | null = null;

  /**
   * Get CSRF token (cached or fetched)
   * Implements request deduplication to avoid multiple simultaneous fetches
   */
  async getCSRFToken(): Promise<string | null> {
    // Return cached token if available
    if (this.csrfToken) {
      return this.csrfToken;
    }

    // If a fetch is already in progress, wait for it
    if (this.tokenFetchPromise) {
      return this.tokenFetchPromise;
    }

    // Fetch token and cache the promise to avoid concurrent requests
    this.tokenFetchPromise = this.fetchCSRFToken();
    const token = await this.tokenFetchPromise;
    this.tokenFetchPromise = null;

    return token;
  }

  /**
   * Fetch CSRF token from backend
   */
  private async fetchCSRFToken(): Promise<string | null> {
    try {
      const env = (typeof process !== 'undefined' ? (process as any).env : {}) || {};
      const apiUrl = env.REACT_APP_API_URL || env.VITE_API_URL || '/api';
      const apiBase = apiUrl.includes('://')
        ? (apiUrl.endsWith('/api') ? apiUrl : apiUrl.replace(/\/$/, '') + '/api')
        : apiUrl;

      const response = await fetch(`${apiBase}/csrf-token`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // Include cookies for CSRF cookie validation
      });

      if (!response.ok) {
        logger.warn(`CSRF token fetch failed: HTTP ${response.status}`);
        return null;
      }

      const data = await response.json();
      this.csrfToken = data.csrfToken;

      logger.debug('CSRF token obtained successfully');
      return this.csrfToken;
    } catch (error) {
      logger.error('CSRF token fetch error:', error);
      return null;
    }
  }

  /**
   * Clear cached token (call this when token expires or on logout)
   */
  clearToken(): void {
    this.csrfToken = null;
  }

  /**
   * Get headers for API requests
   * Includes CSRF token if available
   */
  async getHeadersWithCSRF(baseHeaders: Record<string, string> = {}): Promise<Record<string, string>> {
    const csrfToken = await this.getCSRFToken();

    return {
      ...baseHeaders,
      ...(csrfToken && { 'X-CSRF-Token': csrfToken })
    };
  }
}

// Export singleton instance
export const csrfService = new CSRFService();
